import { useEffect, useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Check, Loader2, Upload } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useProfile } from "@/hooks/use-profile";
import { useWorkspace } from "@/hooks/use-workspace";

const profileSchema = z.object({
  full_name: z.string().trim().min(1, "Full name is required"),
  phone: z.string().trim().optional(),
  workspace: z.string().trim().optional(),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "–";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

export function ProfileTab() {
  const { user } = useAuth();
  const { currentWorkspace, isOwner, updateWorkspace } = useWorkspace();
  const { profile, isLoading, updateProfile, uploadAvatar, removeAvatar } = useProfile();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canManageWorkspace =
    isOwner || currentWorkspace?.created_by_user_id === user?.id;

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { full_name: "", phone: "", workspace: "" },
  });

  // Depend on the primitive field values, not the `profile` object reference:
  // a refetch can hand back a new object with identical data, and resetting on
  // that would discard the user's unsaved edits (and loops the effect).
  useEffect(() => {
    if (profile) {
      form.reset({
        full_name: profile.full_name ?? "",
        phone: profile.phone ?? "",
        workspace: currentWorkspace?.name ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on value changes, not object identity
  }, [profile?.full_name, profile?.phone, currentWorkspace?.name, form]);

  const onSubmit = async (values: ProfileFormValues) => {
    // Persist only the fields the user actually changed so saving one field
    // never overwrites another.
    const dirty = form.formState.dirtyFields;

    const profileUpdate: { full_name?: string; phone?: string | null } = {};
    if (dirty.full_name) profileUpdate.full_name = values.full_name;
    if (dirty.phone) profileUpdate.phone = values.phone?.trim() || null;

    try {
      if (Object.keys(profileUpdate).length > 0) {
        await updateProfile.mutateAsync(profileUpdate);
      }
      if (dirty.workspace && currentWorkspace) {
        await updateWorkspace(currentWorkspace.id, { name: values.workspace?.trim() || "" });
      }
      // Re-baseline the form so the saved values become the new "clean" state.
      form.reset(values);
    } catch {
      // Errors are surfaced via toast inside the mutations; keep the form dirty.
    }
  };

  const handleDiscard = () => {
    form.reset({
      full_name: profile?.full_name ?? "",
      phone: profile?.phone ?? "",
      workspace: currentWorkspace?.name ?? "",
    });
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so re-selecting the same file still fires onChange.
    event.target.value = "";
    if (file) uploadAvatar.mutate(file);
  };

  const currentName = form.watch("full_name") || profile?.full_name || "";
  const isDirty = form.formState.isDirty;
  const isSaving = form.formState.isSubmitting || updateProfile.isPending;
  const isAvatarBusy = uploadAvatar.isPending || removeAvatar.isPending;

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="text-lg">Profile</CardTitle>
        <CardDescription>Update your personal information and how you appear in Syntra.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-16 w-16 rounded-2xl" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Avatar editor */}
              <div className="flex items-center gap-5">
                <Avatar className="h-16 w-16 rounded-2xl">
                  <AvatarImage src={profile?.avatar_url ?? undefined} alt={currentName} className="object-cover" />
                  <AvatarFallback className="rounded-2xl bg-gradient-to-br from-syntra-red to-syntra-red-dark text-xl font-bold tracking-wide text-white">
                    {initials(currentName)}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarChange}
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isAvatarBusy}
                    >
                      {uploadAvatar.isPending ? (
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-1.5" />
                      )}
                      Upload photo
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                      onClick={() => removeAvatar.mutate()}
                      disabled={isAvatarBusy || !profile?.avatar_url}
                    >
                      Remove
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    JPG, PNG or GIF · max 2&nbsp;MB. Initials shown until you add one.
                  </p>
                </div>
              </div>

              {/* Fields */}
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="full_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                        Full name
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Jane Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                        Phone
                      </FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="(205) 555-0142" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="workspace"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                        Workspace name
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="My Workspace" disabled={!canManageWorkspace} {...field} />
                      </FormControl>
                      {!canManageWorkspace && (
                        <p className="text-xs text-muted-foreground">
                          Only the workspace owner can rename it.
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Footer */}
              <div className="flex items-center gap-3 border-t border-border pt-5">
                <span className="mr-auto flex items-center gap-2 text-sm text-muted-foreground">
                  <span
                    className={cn(
                      "h-2 w-2 rounded-full",
                      isDirty ? "bg-warning" : "bg-success",
                    )}
                  />
                  {isDirty ? "Unsaved changes" : "All changes saved"}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDiscard}
                  disabled={!isDirty || isSaving}
                >
                  Discard
                </Button>
                <Button type="submit" disabled={!isDirty || isSaving}>
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  Save changes
                </Button>
              </div>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  );
}
